
    // IMPORTS
    import { computed, defineComponent, PropType, ref } from 'vue';
    import { useRoute } from 'vue-router';
    import router from '@/router';
    import { buildLocalization, navigateToFallbackRoute } from '@/util'
    import { useUserStore } from '@/store/user'
    import { useShoppingCartStore } from '@/store/shoppingCart';

    import NavigationBar from '@/components/navigation-bar.vue'
    import PortalNotification from './portal-notification.vue'
    import SiteTitle from './site-title.vue'
    import TopBannerToolbar from './top-banner-toolbar.vue'

    import { INavigationItem } from '@/types/INavigationItem'
    import { useBrowserStateStore } from '@/store/browserState';

    export default defineComponent({
        name: 'topBanner',

        components: {
            NavigationBar,
            PortalNotification,
            SiteTitle,
            TopBannerToolbar
        },

        props: {
            title: { type: String as PropType<string | null>, required: false, default: () => null },
            showBackButton: { type: Boolean as PropType<boolean>, required: false, default: () => false },
            showLogOff: { type: Boolean as PropType<boolean>, required: false, default: () => false },
            navigationItems: { type: Array as PropType<INavigationItem[]>, required: false, default: () => [] }
        },

        setup(props, { slots }) {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const route = useRoute()
            const { localize } = buildLocalization()
            const userStore = useUserStore()
            const shoppingCartStore = useShoppingCartStore()
            const browserState = useBrowserStateStore()

            const logoffMenuItem = {
                textName: 'LogOff',
                routeName: 'logoff'
            } as INavigationItem      
            
            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const dbName = ref<string | null>(null)
            const publicData = { dbName }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const userData = computed(() => userStore.userData)
            const email = computed(() => userData.value?.currentUser)
            const currentUser = computed(() => userData.value?.partnerAccount.userAccounts.find(u => u.email == email.value))
            const fullName = computed(() => currentUser.value == null
                ? null
                : `${currentUser.value?.firstName} ${currentUser.value?.lastName}`
            )
            const shoppingCart = computed(() => currentUser.value == null
                ? null
                : shoppingCartStore.shoppingCart
            )
            const shoppingCartItemCount = computed(() => shoppingCart.value?.items.reduce((acc, item) => acc + item.count, 0))

            const effectiveNavigationItems = computed(() => browserState.isSmallScreen && props.showLogOff
                ? [...props.navigationItems, logoffMenuItem]
                : props.navigationItems
            )

            const isSmallScreen = computed(() => browserState.isSmallScreen)
            const renderCustomNavigationSlot = computed(() => !!slots.navigation)

            const computedData = {
                userData,
                email,
                currentUser,
                fullName,
                shoppingCart,
                shoppingCartItemCount,
                effectiveNavigationItems,
                isSmallScreen,
                renderCustomNavigationSlot
            }

            /********************************************************************************************************************
             PUBLIC METHODS AND EVENT HANDLERS
             ********************************************************************************************************************/
            function goBack() {
                if (window.history.state?.back) {
                    router.go(-1)
                } else {
                    navigateToFallbackRoute(router, route)
                }
            }

            const publicMethods = { localize, goBack }
            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
